<template>
  <div>
    <vue-headful
      :title="
        getIsDuplicate ? this.setPageTitle('scoringProfilesModelCreate') : this.setPageTitle('scoringProfilesModelEdit')
      "
    />
    <loading :active="getLoading" :color="'blue'" is-full-page loader="spinner"> </loading>

    <b-container class="my-4">
      <!-- HEADER -->
      <custom-flex-header :parent-node-class="['my-4']">
        <template v-slot:left-content>
          <h1 class="mb-0 h4 font-weight-normal">
            <template v-if="getIsDuplicate">{{ $t('scoringProfilesModelCreate.title') }}</template>
            <template v-else>{{ $t('scoringProfilesModelEdit.title') }}</template>
          </h1>
        </template>
        <template v-slot:right-content>
          <b-button size="sm" variant="outline-primary" class="mr-1" @click="onCancel">
            {{ $t('common.cancel') }}
          </b-button>
          <b-button size="sm" variant="primary" :disabled="!getDiff" @click="onSave">
            {{ $t('common.save') }}
          </b-button>
        </template>
      </custom-flex-header>

      <!-- NAME -->
      <b-row class="mb-4">
        <b-col>
          <div class="d-flex">
            <div class="mr-2 w-50">
              <span>{{ $t('common.name') }}</span>
              <b-input
                size="sm"
                type="text"
                autocomplete="off"
                :formatter="nameFormatter"
                v-model="scoringProfileModelName"
                :placeholder="$t('scoringProfilesModelCreate.modelName')"
              ></b-input>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- ASSOCIATED PROFILES -->
      <b-row class="mb-4" v-if="!getIsDuplicate">
        <b-col>
          <custom-collapse
            :toggle="getScoringProfileAssociated.length > 0"
            :title="$tc('scoringProfilesModelEdit.associatedProfiles', getScoringProfileAssociated.length)"
          >
            <template v-slot:content>
              <b-row>
                <b-col>
                  <ol class="mb-0">
                    <li v-for="(el, index) in getScoringProfileAssociated" :key="index">
                      {{ el }}
                    </li>
                  </ol>
                </b-col>
              </b-row>
            </template>
          </custom-collapse>
        </b-col>
      </b-row>

      <!-- TABS -->
      <b-row class="mb-4">
        <b-col>
          <b-tabs v-model="tabIndex" class="fairmat-primary-tabs" @activate-tab="onActivateTab">
            <b-tab v-for="(tab, index) in tabs" :key="index" :title="$t(tab.title)">
              <component v-bind:is="tab.type"></component>
            </b-tab>
          </b-tabs>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import CustomCollapse from '../../components/Lib/CustomCollapse.vue'
import TabScoringModelDrivers from './TabScoringModelDrivers.vue'
import TabScoringModelWeights from './TabScoringModelWeights.vue'
import CustomFlexHeader from '../../components/Lib/CustomFlexHeader.vue'
import { MODEL_TABS } from '../../settings/scoringProfilesSettings'
import { prepareModalList } from '../../utils/modals'
import { createNamespacedHelpers } from 'vuex'
import unsavedChangesMixin from '../../mixins/unsavedChanges'
import { scoringModelSnapshot } from '../../utils/unsavedChangesSnapshots'
const { mapGetters, mapMutations, mapActions } = createNamespacedHelpers('scoringProfilesModelsEditing')

export default {
  mixins: [unsavedChangesMixin],

  components: {
    CustomCollapse,
    CustomFlexHeader,
    TabScoringModelDrivers,
    TabScoringModelWeights,
  },

  props: {
    id: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      copy: 'copy_',
      tabIndex: 0,
      tabs: MODEL_TABS,
      maxModelNameLength: 128,
    }
  },

  computed: {
    ...mapGetters([
      'getLoading',
      'getIsDuplicate',
      'getScoringProfileModelId',
      'getScoringProfileModelName',
      'getScoringProfileModelDiff',
      'getValidDriversFamiliesWeights',
      'getScoringProfileModelNameValidation',
      'getScoringProfileSelectedFamilies',
      'getScoringProfileAssociated',
      'getModelFamiliesIndicatorsWeightsValidation',
    ]),

    getProfileId: {
      get() {
        let id = this.id
        return id.replace(this.copy, '')
      },
    },

    getDiff: {
      get() {
        return this.getIsDuplicate || this.getScoringProfileModelDiff
      },
    },

    scoringProfileModelName: {
      get() {
        const name = this.getScoringProfileModelName
        return this.getEditingMode() !== 'duplicate' ? name : this.formatScoringProfileModelName(name)
      },
      set(newVal) {
        this.setScoringProfileModelName(this.nameFormatter(newVal))
      },
    },
  },

  methods: {
    unsavedChangesSnapshot() {
      return scoringModelSnapshot(this)
    },

    unsavedChangesReady() {
      return !this.getLoading
    },

    ...mapActions([
      'saveScoringProfileModel',
      'loadScoringProfileModel',
      'loadDriversFamiliesIndicators',
      'setDefaultScoringProfileModel',
    ]),
    ...mapMutations(['setScoringProfileModelName', 'setScoringProfileAssociated']),

    onActivateTab(tabIndex, prevTabIndex, evActiveTab) {},

    getEditingMode() {
      return this.id.includes(this.copy) ? 'duplicate' : 'update'
    },

    nameFormatter(value) {
      return value.substring(0, this.maxModelNameLength)
    },

    formatScoringProfileModelName(name) {
      return name.replace(/Copy|Copia/, this.$t('scoringProfileEdit.copy'))
    },

    onSave() {
      let isValidName = this.getScoringProfileModelNameValidation
      const driversFamiliesWeights = this.getValidDriversFamiliesWeights
      const prevSelectedDriverFamilies = this.getScoringProfileSelectedFamilies
      const noEmptyFamily =
        this.getIsDuplicate || this.getScoringProfileAssociated.length == 0
          ? true
          : prevSelectedDriverFamilies.length == driversFamiliesWeights.length
      const atLeastOneIndicatorMustBeSelected = driversFamiliesWeights.length > 0
      const isValidDriversFamiliesTotalWeight = driversFamiliesWeights.every((j) => j.isValidWeight)
      const results = this.$store.getters['scoringProfilesCommon/getScoringProfileDefinitionValidation']
      const modelName = this.getScoringProfileModelName
      // This check is needed to permit the value 'Custom profile' in the custom multiselect
      // included in TabScoringDefiniton.vue for the choice of the definition type and the
      // possibility to have a default value for custom scoring definition
      const invalidNames = [
        this.$i18n.messages['en'].scoringProfileDetails.customDefinition.toLowerCase(),
        this.$i18n.messages['it'].scoringProfileDetails.customDefinition.toLowerCase(),
      ]

      const getModelFamiliesIndicatorsWeightsValidation = this.getModelFamiliesIndicatorsWeightsValidation

      if (!isValidName) {
        this.$bvToast.toast(this.$t('scoringProfilesModelEdit.modalMessages.errorOnMissingName'), {
          title: this.$t('scoringProfilesModels.title'),
          variant: 'danger',
        })
      }

      if (invalidNames.includes(modelName.toLowerCase())) {
        isValidName = false
        this.$bvToast.toast(
          this.$t('scoringProfilesModelEdit.modalMessages.errorOnInvalidName', { invalidName: modelName }),
          {
            title: this.$t('scoringProfilesModels.title'),
            variant: 'danger',
          }
        )
      }

      if (!atLeastOneIndicatorMustBeSelected) {
        this.$bvToast.toast(this.$t('scoringProfilesModelEdit.modalMessages.atLeastOneIndicatorMustBeSelected'), {
          title: this.$t('scoringProfilesModels.title'),
          variant: 'danger',
        })
      }

      // Inserire messaggio
      if (!noEmptyFamily) {
        this.$bvToast.toast(this.$t('scoringProfilesModelEdit.modalMessages.errorOnEmptyFamily'), {
          title: this.$t('scoringProfilesModels.title'),
          variant: 'danger',
        })
      }

      if (atLeastOneIndicatorMustBeSelected && !results.isValidScoringDefinitionTotalWeight) {
        this.$bvToast.toast(this.$t('scoringProfiles.errorOnTotalWeight'), {
          title: this.$t('scoringProfilesModels.title'),
          variant: 'danger',
        })
      }

      if (!results.isDriversWeightsGreaterThanZero) {
        this.$bvToast.toast(this.$t('scoringProfiles.weightsMustBeGreaterThan0'), {
          title: this.$t('scoringProfilesModels.title'),
          variant: 'danger',
        })
      }

      if (!getModelFamiliesIndicatorsWeightsValidation.validate) {
        getModelFamiliesIndicatorsWeightsValidation.indicators.forEach((item) => {
          const indicator = item.indicator
          const str = this.$t('drivers.codes.' + indicator.toUpperCase())
          this.$bvToast.toast(this.$t('scoringProfilesModelEdit.modalMessages.invalidDriverWeight', { driver: str }), {
            title: this.$t('scoringProfilesModels.title'),
            variant: 'danger',
          })
        })
      }

      if (!isValidDriversFamiliesTotalWeight) {
        const categories = driversFamiliesWeights.filter((k) => !k.isValidWeight)
        categories.forEach((z) => {
          const family = z.family
          const str = this.$t('drivers.categories.' + family.toUpperCase())
          this.$bvToast.toast(this.$t('scoringProfilesModelEdit.modalMessages.invalidDriverWeight', { driver: str }), {
            title: this.$t('scoringProfilesModels.title'),
            variant: 'danger',
          })
        })
      }

      const isValid =
        isValidName &&
        atLeastOneIndicatorMustBeSelected &&
        isValidDriversFamiliesTotalWeight &&
        results.isValidScoringDefinitionTotalWeight &&
        results.isDriversWeightsGreaterThanZero &&
        noEmptyFamily &&
        getModelFamiliesIndicatorsWeightsValidation.validate

      if (isValid) {
        if (!this.getScoringProfileModelId) {
          this.saveModel()
        } else {
          const message = prepareModalList(
            this.$t('scoringProfilesModelsList.modalMessages.confirmUpdate'),
            this.getScoringProfileAssociated
          )
          const messageNodes = this.$createElement('div', { domProps: { innerHTML: message } })
          // No linked models
          if (Array.isArray(this.getScoringProfileAssociated) && this.getScoringProfileAssociated.length === 0) {
            this.saveModel()
          } else {
            this.$bvModal.msgBoxConfirm([messageNodes], { cancelTitle: this.$t('common.cancel') }).then((value) => {
              if (value) {
                this.saveModel()
              }
            })
          }
        }
      }
    },

    saveModel() {
      this.saveScoringProfileModel()
        .then(() => {
          this.$router.push({ name: 'scoringProfilesModelsList' })
        })
        .catch(async (error) => {
          const err = await error.response
          const message = await err.text()
          if (message.includes('NameInUse')) {
            this.$bvToast.toast(this.$t('scoringProfiles.nameInUse'), {
              title: this.$t('scoringProfiles.title'),
              variant: 'danger',
            })
          } else if (message.includes('ScoringDefinitionWeightInvalid')) {
            this.$bvToast.toast(this.$t('scoringProfiles.scoringDefinitionWeightInvalid'), {
              title: this.$t('scoringProfiles.title'),
              variant: 'danger',
            })
          } else if (message.includes('DriverFamiliesLockedByScoringProfiles')) {
            // The drivers tab already locks the families when profiles are linked; this covers a
            // profile linked after the page was opened.
            this.$bvToast.toast(this.$t('scoringProfilesModelEdit.modalMessages.errorFamiliesLocked'), {
              title: this.$t('scoringProfilesModels.title'),
              variant: 'danger',
            })
          } else {
            this.$bvModal.msgBoxOk(this.$t('common.genericError'))
          }
        })
    },

    onCancel() {
      if (this.getDiff) {
        this.$bvModal
          .msgBoxConfirm(this.$t('scoringProfilesList.modalMessages.confirmUnsaved'), {
            cancelTitle: this.$t('common.no'),
            okTitle: this.$t('common.yes'),
          })
          .then((value) => {
            // OK = value = true
            if (value) {
              this.$router.push({ name: 'scoringProfilesModelsList' })
            }
          })
      } else {
        this.$router.push({ name: 'scoringProfilesModelsList' })
      }
    },
  },

  created() {
    this.setDefaultScoringProfileModel()
    this.loadScoringProfileModel({ id: this.getProfileId, action: this.getEditingMode() })
      .then((model) => {
        this.loadDriversFamiliesIndicators(model).catch((err) => {
          this.httpErrorsHandler(err)
        })
      })
      .catch((err) => {
        this.httpErrorsHandler(err)
      })
    if (this.getEditingMode() === 'update') {
      this.$store
        .dispatch('scoringProfilesModelsList/checkProfileLinking', this.getProfileId)
        .then((data) => {
          this.setScoringProfileAssociated(data)
        })
        .catch((err) => {
          this.httpErrorsHandler(err)
        })
    }
  },
}
</script>

<style lang="scss" scoped></style>
